"use client";

import { useLocale, useTranslations } from "next-intl";
import { Button } from "@qrody/ui";

export interface VanityUpgradeDialogProps {
  open: boolean;
  onClose: () => void;
  appUrl: (path: string) => string;
  persistDraftToStorage: () => void;
}

/**
 * "Pro feature required" upsell shown when an anonymous / free user hits
 * the vanity-stack limit (3 stacked design tweaks) or tries to enable a
 * Pro-only toggle (watermark removal, minVersion, 600 DPI). Click-outside
 * closes, the CTA persists the current editor state to the draft cookie
 * before redirecting to the app-origin login.
 */
export function VanityUpgradeDialog({
  open,
  onClose,
  appUrl,
  persistDraftToStorage,
}: VanityUpgradeDialogProps) {
  const t = useTranslations("generator");
  const locale = useLocale();
  if (!open) return null;
  return (
    <div
      role="alertdialog"
      aria-labelledby="vanity-upgrade-title"
      aria-describedby="vanity-upgrade-body"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-xl border bg-card p-5 shadow-lg">
        <h2 id="vanity-upgrade-title" className="text-base font-semibold">
          {t("pro.upgrade.title")}
        </h2>
        <p id="vanity-upgrade-body" className="mt-2 text-sm text-muted-foreground">
          {t("pro.upgrade.body")}
        </p>
        <div className="mt-4 flex justify-end">
          <Button asChild>
            <a
              href={appUrl(`/${locale}/login?ref=anon-vanity-upgrade`)}
              onClick={persistDraftToStorage}
            >
              {t("pro.upgrade.cta")}
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
